import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import notificationService from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import {
  STALE_TIME_MS,
  queryKeys,
  invalidateNotificationsTree,
} from '../config/reactQuery';

export const useNotificationCounts = () => {
  const { user } = useAuth();
  const { isConnected } = useSocket();

  // Fallback: if Socket.IO is disconnected, do a low-frequency poll to keep counts reasonably fresh.
  const fallbackPollMs = 60_000;
  return useQuery({
    queryKey: ['notificationCounts'],
    queryFn: () => notificationService.getUnreadCounts(),
    staleTime: STALE_TIME_MS.notificationsList,
    refetchInterval: isConnected ? false : fallbackPollMs,
    enabled: !!user,
  });
};

export const useNotificationMutation = () => {
  const queryClient = useQueryClient();

  const markAsViewed = useMutation({
    mutationFn: ({ notificationId, entityType, entityId }: {
      notificationId: string;
      entityType?: string;
      entityId?: string;
    }) => notificationService.markAsViewed(notificationId, entityType, entityId),
    onSuccess: () => {
      invalidateNotificationsTree(queryClient);
    },
  });

  const markEntityAsViewed = useMutation({
    mutationFn: ({ entityType, entityId }: {
      entityType: string;
      entityId: string;
    }) => notificationService.markEntityAsViewed(entityType, entityId),
    onSuccess: () => {
      invalidateNotificationsTree(queryClient);
    },
  });

  return {
    markAsViewed,
    markEntityAsViewed,
    isLoading: markAsViewed.isPending || markEntityAsViewed.isPending,
  };
};

export const useEntityNotifications = (entityType: string, entityId: string) => {
  return useQuery({
    queryKey: ['entityNotifications', entityType, entityId],
    queryFn: () => notificationService.getUnreadForEntity(entityType, entityId),
    enabled: !!entityType && !!entityId,
    staleTime: 30000,
  });
};

export const useNotifications = (page: number = 1, limit: number = 10, isRead?: boolean) => {
  const { organization } = useAuth();
  const orgId = organization?.id ?? null;
  const listFilters = useMemo(() => {
    const filter: 'all' | 'read' | 'unread' =
      isRead === true ? 'read' : isRead === false ? 'unread' : 'all';
    return { limit, filter } as const;
  }, [limit, isRead]);

  return useQuery({
    queryKey: queryKeys.notificationsList(orgId, page, listFilters),
    queryFn: () =>
      notificationService.getNotifications({
        page,
        limit,
        ...(isRead !== undefined && { is_read: isRead }),
      }),
    staleTime: STALE_TIME_MS.notificationsList,
  });
};
